import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './BoostCard.css';
import SoundManager from '../services/SoundManager';
import { getStatusIcon } from './StatusIcons';

// Типы данных для статуса буста
interface BoostStatus {
  type: 'vip' | 'rank' | 'leader';
  current: {
    level: number;
    name: string;
    nameKey?: string | null; // Ключ локализации (опционально)
    icon: string;
    aprBonus: number;
    unlocked: boolean;
  };
  next: {
    level: number;
    name: string;
    icon: string;
    aprBonus: number;
    threshold?: number;
    daysRequired?: number;
    referralsRequired?: number;
  } | null;
  progress: {
    current: number;
    required: number;
    percent: number;
  };
  allLevels: any[];
}

interface BoostCardProps {
  boostStatus: BoostStatus;
  category: 'vip' | 'rank' | 'leader';
}

const BoostCard: React.FC<BoostCardProps> = ({ boostStatus, category }) => {
  const { t } = useTranslation();
  const [showHint, setShowHint] = useState(false);

  // Проверка безопасности - если данные не загружены
  if (!boostStatus || !boostStatus.current) {
    return (
      <div className="boost-card loading">
        <div className="boost-content-center">
          <p>{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  // Определяем цвет градиента для категории
  const getCategoryColor = () => {
    switch (category) {
      case 'vip':
        return 'linear-gradient(135deg, rgba(218, 165, 32, 0.4) 0%, rgba(184, 134, 11, 0.5) 100%)';
      case 'rank':
        return 'linear-gradient(135deg, #4169E1 0%, #1E90FF 100%)';
      case 'leader':
        return 'linear-gradient(135deg, #9370DB 0%, #8A2BE2 100%)';
      default:
        return 'linear-gradient(135deg, #808080 0%, #A9A9A9 100%)';
    }
  };

  // Определяем заголовок категории
  const getCategoryTitle = () => {
    switch (category) {
      case 'vip':
        return t('profile.vipStatus');
      case 'rank':
        return t('profile.rankStatus');
      case 'leader':
        return t('profile.leaderStatus');
      default:
        return '';
    }
  };

  // Определяем описание прогресса
  const getProgressText = () => {
    const { progress, next } = boostStatus;

    if (!next) {
      return t('profile.maxLevel');
    }

    if (category === 'vip') {
      return `${progress.current.toLocaleString()} / ${progress.required.toLocaleString()} SPERM`;
    } else if (category === 'rank') {
      return `${progress.current} / ${progress.required} ${t('profile.days')}`;
    } else if (category === 'leader') {
      return `${progress.current} / ${progress.required} ${t('profile.referrals')}`;
    }
    return '';
  };

  // Определяем текст следующего уровня
  const getNextLevelText = () => {
    const { next } = boostStatus;

    if (!next) {
      return null;
    }

    // Используем локализованное имя следующего уровня
    let nextName = next.name;
    if (category === 'vip') {
      nextName = t(`vipLevels.${next.level}`);
    } else if (category === 'rank') {
      nextName = t(`ranks.${next.level}`);
    } else if (category === 'leader') {
      nextName = t(`leaders.${next.level}`);
    }

    return t('profile.toNextLevel', { name: nextName }) + ' ' + getProgressText();
  };

  return (
    <div
      className={`boost-card ${boostStatus.current.unlocked ? 'unlocked' : 'locked'} ${category} ${showHint ? 'modal-open' : ''}`}
      style={{
        background: boostStatus.current.unlocked ? getCategoryColor() : undefined
      }}
    >
      {/* Статус индикатор в углу */}
      <div className="boost-status">
        {boostStatus.current.unlocked ? (
          <span className="status-icon unlocked">✓</span>
        ) : (
          <span className="status-icon locked">🔒</span>
        )}
      </div>

      {/* Кнопка помощи в углу */}
      <button
        className="help-button"
        onClick={() => {
          SoundManager.playModalOpen();
          setShowHint(!showHint);
        }}
      >
        <span className="help-icon">?</span>
      </button>

      {/* Центрированный контент */}
      <div className="boost-content-center">
        {/* Категория */}
        <h4 className="boost-category-title">{getCategoryTitle()}</h4>

        {/* Иконка */}
        <div className="boost-icon">
          {getStatusIcon(category, boostStatus.current.level, 64)}
        </div>

        {/* Название текущего статуса */}
        <h3 className="boost-name">
          {category === 'vip' && t(`vipLevels.${boostStatus.current.level}`)}
          {category === 'rank' && t(`ranks.${boostStatus.current.level}`)}
          {category === 'leader' && t(`leaders.${boostStatus.current.level}`)}
        </h3>

        {/* APR бонус */}
        {boostStatus.current.unlocked && (
          <p className="boost-bonus">+{boostStatus.current.aprBonus}% APR</p>
        )}

        {/* Прогресс-бар */}
        {boostStatus.next && (
          <div className="boost-progress-section">
            <div className="progress-bar">
              <div
                className="boost-progress-fill"
                style={{
                  width: `${Math.min(boostStatus.progress.percent, 100)}%`,
                  background: boostStatus.current.unlocked ? '#fff' : getCategoryColor()
                }}
              />
            </div>
            <p className="progress-text">{getNextLevelText()}</p>
          </div>
        )}
      </div>

      {/* Модальное окно с полной механикой */}
      {showHint && (
        <div className="hint-modal" onClick={() => { SoundManager.playButtonClick(); setShowHint(false); }}>
          <div className="hint-content" onClick={(e) => e.stopPropagation()}>
            <button className="hint-close" onClick={() => { SoundManager.playButtonClick(); setShowHint(false); }}>✕</button>
            <h3>{t('profile.allLevels', { category: getCategoryTitle().toLowerCase() })}</h3>

            <div className="all-levels-list">
              {boostStatus.allLevels.map((level, index) => (
                <div
                  key={index}
                  className={`level-item ${level.level === boostStatus.current.level ? 'current' : ''}`}
                >
                  <div className="level-icon">
                    {getStatusIcon(category, level.level, 32)}
                  </div>
                  <div className="level-info">
                    <p className="level-name">
                      {category === 'vip' && t(`vipLevels.${level.level}`)}
                      {category === 'rank' && t(`ranks.${level.level}`)}
                      {category === 'leader' && t(`leaders.${level.level}`)}
                    </p>
                    <p className="level-requirement">
                      {category === 'vip' && `${t('profile.from')} ${level.threshold?.toLocaleString()} SPERM`}
                      {category === 'rank' && `${level.daysRequired} ${t('profile.days')}`}
                      {category === 'leader' && `${level.referralsRequired} ${t('profile.referrals')}`}
                    </p>
                  </div>
                  <span className="level-bonus">+{level.aprBonus}% APR</span>
                </div>
              ))}
            </div>

            <div className="hint-progress">
              <p><strong>{t('profile.yourProgress')}</strong></p>
              <p>{getProgressText()}</p>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${Math.min(boostStatus.progress.percent, 100)}%`,
                    background: getCategoryColor()
                  }}
                />
              </div>
            </div>

            {/* Примечание для лидерского статуса */}
            {category === 'leader' && (
              <div className="leader-note">
                <span className="note-icon">ℹ️</span>
                <p>{t('profile.leaderActiveReferralsNote')}</p>
              </div>
            )}

            <button className="hint-ok-button" onClick={() => { SoundManager.playButtonClick(); setShowHint(false); }}>
              {t('profile.gotIt')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoostCard;
